import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api/axios';

const DEFAULT_OPTIONS = 'Strongly Disagree\nDisagree\nNeutral\nAgree\nStrongly Agree';

function normalizeOptions(question, questionnaire) {
  let options = question.options;
  if (typeof options === 'string') {
    try {
      options = JSON.parse(options);
    } catch (_) {
      options = options.split(/[,;\n]/).map((option) => option.trim()).filter(Boolean);
    }
  }

  if (Array.isArray(options) && options.length > 0) return options;
  if (String(question.question_type).toUpperCase() !== 'RATING') return [];

  const minimum = Number(questionnaire?.scale_min ?? 1);
  const maximum = Number(questionnaire?.scale_max ?? 5);
  return Array.from({ length: Math.max(0, maximum - minimum + 1) }, (_, index) => ({
    label: String(minimum + index),
    value: minimum + index,
  }));
}

function QuestionnaireQuestionPreview({ question, index, questionnaire }) {
  const type = String(question.question_type || 'MCQ').toUpperCase();
  const options = normalizeOptions(question, questionnaire);

  return (
    <fieldset className="rounded-lg border border-slate-200 bg-white p-4 sm:p-5">
      <legend className="sr-only">Question {index + 1}</legend>
      <div className="flex items-start gap-2">
        <span className="mt-0.5 text-sm font-semibold text-slate-500" aria-hidden="true">{index + 1}.</span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium leading-6 text-slate-900">
            {question.question_text}
            {question.is_required && <span className="ml-1 text-red-600" title="Required">*</span>}
          </p>
          <p className="mt-0.5 text-xs text-slate-500">
            {type === 'TEXT' ? 'Written response' : type === 'RATING' ? 'Select one rating' : 'Select one option'}
            {question.weight != null && ` | Weight ${question.weight}`}
          </p>

          {type === 'TEXT' ? (
            <textarea
              readOnly
              rows={3}
              className="input mt-3 w-full resize-none text-sm"
              placeholder="Response will be entered here"
              aria-label={`Preview response for question ${index + 1}`}
            />
          ) : options.length > 0 ? (
            <div className={`mt-3 grid gap-2 ${type === 'RATING' && options.length <= 5 ? 'grid-cols-2 sm:grid-cols-5' : 'sm:grid-cols-2'}`}>
              {options.map((option, optionIndex) => {
                const isObject = typeof option === 'object' && option !== null;
                const label = isObject ? option.label : option;
                const value = isObject && option.value != null ? option.value : label;
                return (
                  <label
                    key={`${String(label)}-${optionIndex}`}
                    className="flex min-h-[2.75rem] cursor-pointer items-center gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 transition-colors hover:border-indigo-300 hover:bg-indigo-50"
                  >
                    <input
                      type="radio"
                      name={`question-preview-${question.id || index}`}
                      value={String(value)}
                      className="h-4 w-4 shrink-0 border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="min-w-0 flex-1 leading-5">{label}</span>
                    {isObject && option.value != null && String(option.value) !== String(option.label) && (
                      <span className="rounded bg-white px-1.5 py-0.5 text-[0.68rem] font-medium text-slate-500" title="Score value">
                        {option.value}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          ) : (
            <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              No response options have been configured for this question.
            </p>
          )}
        </div>
      </div>
    </fieldset>
  );
}

export default function FeedbackQuestionnairePage() {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'preview';
  const initialStageId = searchParams.get('stageId') || '';
  const initialQuestionnaireId = searchParams.get('questionnaireId') || '';
  const instituteId = searchParams.get('instituteId') || '';
  const apiParams = useMemo(() => (instituteId ? { instituteId } : {}), [instituteId]);

  const [stages, setStages] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [selectedStageId, setSelectedStageId] = useState(initialStageId);
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState(initialQuestionnaireId);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [manualQuestion, setManualQuestion] = useState({
    questionText: '',
    questionType: 'MCQ',
    optionsText: DEFAULT_OPTIONS,
    weight: 1,
    qualitativeGroup: '',
  });

  const selectedStage = stages.find((stage) => stage.id === selectedStageId);
  const stageQuestionnaires = questionnaires.filter((q) => !selectedStageId || q.stage_id === selectedStageId);
  const canEdit = mode === 'edit';

  const loadQuestionnaires = async () => {
    const params = { ...apiParams, ...(selectedStageId ? { stageId: selectedStageId } : {}) };
    const res = await api.get('/admin/feedback/questionnaires', { params });
    setQuestionnaires(res.data?.data || []);
  };

  const loadPreview = async (id = selectedQuestionnaireId) => {
    if (!id) {
      setPreview(null);
      return;
    }
    const res = await api.get(`/admin/feedback/questionnaires/${id}/preview`, { params: apiParams });
    setPreview(res.data?.data || null);
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setMessage('');
      try {
        const [stagesRes, questionnairesRes] = await Promise.all([
          api.get('/admin/feedback/lifecycle', { params: apiParams }),
          api.get('/admin/feedback/questionnaires', {
            params: { ...apiParams, ...(initialStageId ? { stageId: initialStageId } : {}) },
          }),
        ]);
        if (cancelled) return;
        const nextStages = stagesRes.data?.data || [];
        const nextQuestionnaires = questionnairesRes.data?.data || [];
        setStages(nextStages);
        setQuestionnaires(nextQuestionnaires);
        const nextStageId = initialStageId || nextQuestionnaires[0]?.stage_id || nextStages[0]?.id || '';
        const nextQuestionnaireId = initialQuestionnaireId || nextQuestionnaires[0]?.id || '';
        setSelectedStageId(nextStageId);
        setSelectedQuestionnaireId(nextQuestionnaireId);
        if (nextQuestionnaireId) {
          const previewRes = await api.get(`/admin/feedback/questionnaires/${nextQuestionnaireId}/preview`, { params: apiParams });
          if (!cancelled) setPreview(previewRes.data?.data || null);
        }
      } catch (err) {
        if (!cancelled) setMessage(err?.response?.data?.error?.message || 'Unable to load questionnaire.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [apiParams, initialQuestionnaireId, initialStageId]);

  const selectQuestionnaire = async (id) => {
    setSelectedQuestionnaireId(id);
    await loadPreview(id);
  };

  const createQuestionnaire = async (e) => {
    e.preventDefault();
    if (!selectedStageId || !newTitle.trim()) return;
    const res = await api.post('/admin/feedback/questionnaires', {
      ...apiParams,
      stageId: selectedStageId,
      title: newTitle.trim(),
    });
    const created = res.data?.data;
    setNewTitle('');
    setMessage('Questionnaire created.');
    await loadQuestionnaires();
    if (created?.id) await selectQuestionnaire(created.id);
  };

  const addQuestion = async (e) => {
    e.preventDefault();
    if (!selectedQuestionnaireId) return;
    const options = manualQuestion.optionsText.split('\n').map((item) => item.trim()).filter(Boolean);
    await api.post(`/admin/feedback/questionnaires/${selectedQuestionnaireId}/questions`, {
      questions: [{
        questionText: manualQuestion.questionText,
        questionType: manualQuestion.questionType,
        options,
        weight: Number(manualQuestion.weight || 1),
        qualitativeGroup: manualQuestion.qualitativeGroup,
      }],
    });
    setManualQuestion((prev) => ({ ...prev, questionText: '' }));
    setMessage('Question added.');
    await loadPreview();
    await loadQuestionnaires();
  };

  const uploadQuestions = async () => {
    if (!selectedQuestionnaireId || !uploadFile) return;
    const formData = new FormData();
    formData.append('file', uploadFile);
    await api.post(`/admin/feedback/questionnaires/${selectedQuestionnaireId}/upload`, formData, {
      params: apiParams,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    setUploadFile(null);
    setMessage('Questions uploaded.');
    await loadPreview();
    await loadQuestionnaires();
  };

  const updateStatus = async (status) => {
    if (!selectedQuestionnaireId) return;
    await api.post(`/admin/feedback/questionnaires/${selectedQuestionnaireId}/status`, { status }, { params: apiParams });
    setMessage(`Questionnaire marked ${status.toLowerCase()}.`);
    await loadPreview();
    await loadQuestionnaires();
  };

  const downloadTemplate = async () => {
    const res = await api.get('/admin/feedback/questionnaires/template', { responseType: 'blob', params: apiParams });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'feedback-questionnaire-template.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <main className="min-h-screen bg-slate-50 p-4 sm:p-6">
      <div className="mx-auto max-w-6xl space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <Link to="/dashboard/admin?tab=feedback-lifecycle" className="text-sm font-medium text-primary-600 hover:underline">Back to Feedback Lifecycle</Link>
            <h1 className="mt-2 text-2xl font-semibold text-slate-900">
              {canEdit ? 'Change Questionnaire' : 'Questionnaire Preview'}
            </h1>
            <p className="text-sm text-slate-500">{selectedStage ? `${selectedStage.sequence_no}. ${selectedStage.name}` : 'Feedback step questionnaire'}</p>
          </div>
          {canEdit && (
            <button type="button" onClick={downloadTemplate} className="btn-secondary text-sm">Download Excel template</button>
          )}
        </div>

        {message && <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-800">{message}</div>}
        {loading ? (
          <div className="card card-body text-sm text-slate-500">Loading questionnaire...</div>
        ) : (
          <div className="grid gap-5 lg:grid-cols-[20rem_minmax(0,1fr)]">
            <aside className="card card-body space-y-4">
              <div>
                <label className="input-label">Feedback step</label>
                <select
                  value={selectedStageId}
                  onChange={async (e) => {
                    setSelectedStageId(e.target.value);
                    setSelectedQuestionnaireId('');
                    setPreview(null);
                    const res = await api.get('/admin/feedback/questionnaires', { params: { ...apiParams, stageId: e.target.value } });
                    setQuestionnaires(res.data?.data || []);
                  }}
                  className="input w-full text-base text-slate-900 text-left"
                  disabled={!canEdit}
                >
                  {stages.map((stage) => <option key={stage.id} value={stage.id}>{stage.sequence_no}. {stage.name}</option>)}
                </select>
              </div>
              <div>
                <label className="input-label">Questionnaire</label>
                <select value={selectedQuestionnaireId} onChange={(e) => selectQuestionnaire(e.target.value)} className="input w-full text-base text-slate-900 text-left">
                  <option value="">Select questionnaire</option>
                  {stageQuestionnaires.map((q) => (
                    <option key={q.id} value={q.id}>{q.title} ({q.status})</option>
                  ))}
                </select>
              </div>
              {canEdit && (
                <form onSubmit={createQuestionnaire} className="space-y-3 border-t border-slate-200 pt-4">
                  <h2 className="text-sm font-semibold text-slate-800">Create for this step</h2>
                  <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} className="input w-full text-base text-slate-900 text-left" placeholder="Questionnaire title" />
                  <button type="submit" className="btn-primary text-sm w-full">Create questionnaire</button>
                </form>
              )}
            </aside>

            <section className="space-y-5">
              {canEdit && selectedQuestionnaireId && (
                <div className="grid gap-5 lg:grid-cols-2">
                  <form onSubmit={addQuestion} className="card card-body space-y-3">
                    <h2 className="text-base font-semibold text-slate-800">Add question directly</h2>
                    <input value={manualQuestion.questionText} onChange={(e) => setManualQuestion((prev) => ({ ...prev, questionText: e.target.value }))} className="input w-full text-base text-slate-900 text-left" placeholder="Question text" required />
                    <select value={manualQuestion.questionType} onChange={(e) => setManualQuestion((prev) => ({ ...prev, questionType: e.target.value }))} className="input w-full text-base text-slate-900 text-left">
                      <option value="MCQ">MCQ</option>
                      <option value="RATING">Rating</option>
                      <option value="TEXT">Text</option>
                    </select>
                    <textarea value={manualQuestion.optionsText} onChange={(e) => setManualQuestion((prev) => ({ ...prev, optionsText: e.target.value }))} className="input min-h-[120px] w-full text-base text-slate-900 text-left" placeholder="One option per line" />
                    <div className="grid grid-cols-2 gap-3">
                      <input type="number" min="0" step="0.1" value={manualQuestion.weight} onChange={(e) => setManualQuestion((prev) => ({ ...prev, weight: e.target.value }))} className="input w-full text-base text-slate-900 text-left" placeholder="Weight" />
                      <input value={manualQuestion.qualitativeGroup} onChange={(e) => setManualQuestion((prev) => ({ ...prev, qualitativeGroup: e.target.value }))} className="input w-full text-base text-slate-900 text-left" placeholder="Analysis group" />
                    </div>
                    <button type="submit" className="btn-primary text-sm w-full">Add question</button>
                  </form>
                  <div className="card card-body space-y-3">
                    <h2 className="text-base font-semibold text-slate-800">Upload Excel questions</h2>
                    <input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} className="block w-full text-sm text-slate-600" />
                    <button type="button" onClick={uploadQuestions} disabled={!uploadFile} className="btn-secondary text-sm w-full disabled:opacity-50">Upload questions</button>
                    <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-3">
                      <button type="button" onClick={() => updateStatus('ACTIVE')} className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-medium">Activate</button>
                      <button type="button" onClick={() => updateStatus('DRAFT')} className="px-3 py-1.5 rounded-lg bg-slate-700 text-white text-xs font-medium">Draft</button>
                      <button type="button" onClick={() => updateStatus('ARCHIVED')} className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-medium">Archive</button>
                    </div>
                  </div>
                </div>
              )}

              <div className="card card-body space-y-4">
                {!preview ? (
                  <p className="text-sm text-slate-500">Select a questionnaire to preview.</p>
                ) : (
                  <>
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900">{preview.questionnaire.title}</h2>
                      <p className="text-sm text-slate-500">{preview.questionnaire.stage_name} | {preview.questionnaire.status}</p>
                    </div>
                    <div className="space-y-3">
                      {preview.questions.map((question, index) => (
                        <QuestionnaireQuestionPreview
                          key={question.id || index}
                          question={question}
                          index={index}
                          questionnaire={preview.questionnaire}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </section>
          </div>
        )}
      </div>
    </main>
  );
}
